import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Card, Text, Button, TextField, Banner, BlockStack, InlineStack,
    Checkbox, Box, Spinner, Badge,
} from '@shopify/polaris';
import { authenticatedFetch } from './AuthComponent';
import { STORE_DISPLAY_NAMES } from './StoreContext';

// Settings → Team Portal tab. Admin-only. Single-table UI (2026-08-26 —
// replaced the previous two-column drag-and-drop):
//   Rows    = every (store, club, fulfillment loc) tuple enumerated from
//             order_cache (via /api/settings/tp-overrides/clubs), merged with
//             the saved override rows (operations_tp_override_clubs).
//   Columns = Number Override + Team Override checkboxes.
// Check/uncheck freely, then press Apply — a DELTA of changed clubs goes to
// POST /api/settings/tp-overrides/apply (either flag on → upsert; both off →
// row deleted). Sort: clubs with either override first (A-Z), then the rest
// (A-Z) — frozen at load so rows don't jump while you're clicking.

const clubKey = (c) => `${c.store_key}|${c.shopify_location_id}|${c.club_name.toLowerCase()}`;

export default function TeamPortalTab() {
    const [rows, setRows]         = useState([]);     // merged club records (sorted, frozen)
    const [flags, setFlags]       = useState({});     // key → {number, team} (UI state)
    const [orig, setOrig]         = useState({});     // key → {number, team} (as loaded)
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);
    const [err, setErr]           = useState(null);
    const [notice, setNotice]     = useState(null);
    const [filter, setFilter]     = useState('');

    const loadAll = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const [rc, ro] = await Promise.all([
                authenticatedFetch('/api/settings/tp-overrides/clubs'),
                authenticatedFetch('/api/settings/tp-overrides'),
            ]);
            if (!rc.ok || !ro.ok) throw new Error('load failed');
            const [jc, jo] = await Promise.all([rc.json(), ro.json()]);
            const clubs = jc.clubs || [];
            const overrides = jo.rows || [];

            // Merge: enumerated clubs + any saved override rows that didn't
            // enumerate (older seasons out of the enum window) so they can
            // still be turned off.
            const byKey = new Map();
            for (const c of clubs) byKey.set(clubKey(c), { ...c });
            for (const o of overrides) {
                const k = clubKey(o);
                if (!byKey.has(k)) byKey.set(k, { ...o });
            }

            const f = {};
            for (const o of overrides) {
                f[clubKey(o)] = {
                    number: o.number_override !== false,   // legacy rows = number
                    team:   o.team_override === true,
                };
            }
            const merged = [...byKey.values()];
            // Sort ONCE at load: overridden clubs first (A-Z), rest A-Z.
            merged.sort((a, b) => {
                const fa = f[clubKey(a)], fb = f[clubKey(b)];
                const ea = !!(fa && (fa.number || fa.team));
                const eb = !!(fb && (fb.number || fb.team));
                if (ea !== eb) return ea ? -1 : 1;
                const n = a.club_name.localeCompare(b.club_name, undefined, { sensitivity: 'base' });
                if (n !== 0) return n;
                return (a.soccer_location_name || '').localeCompare(b.soccer_location_name || '');
            });
            setRows(merged);
            setFlags(f);
            setOrig(f);
        } catch (e) {
            setErr(String(e.message || e));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { loadAll(); }, [loadAll]);

    const getF = useCallback((k) => flags[k] || { number: false, team: false }, [flags]);
    const toggle = useCallback((k, which) => {
        setNotice(null);
        setFlags(prev => {
            const cur = prev[k] || { number: false, team: false };
            return { ...prev, [k]: { ...cur, [which]: !cur[which] } };
        });
    }, []);

    // Delta = keys whose flags differ from the loaded snapshot.
    const dirtyKeys = useMemo(() => {
        const keys = new Set([...Object.keys(flags), ...Object.keys(orig)]);
        const out = [];
        for (const k of keys) {
            const a = flags[k] || { number: false, team: false };
            const b = orig[k]  || { number: false, team: false };
            if (!!a.number !== !!b.number || !!a.team !== !!b.team) out.push(k);
        }
        return out;
    }, [flags, orig]);

    const apply = useCallback(async () => {
        if (!dirtyKeys.length) return;
        setSaving(true); setErr(null); setNotice(null);
        try {
            const byKey = new Map(rows.map(r => [clubKey(r), r]));
            const payload = dirtyKeys.map(k => {
                const c = byKey.get(k);
                const f = flags[k] || { number: false, team: false };
                return {
                    store_key:            c.store_key,
                    club_id:              c.club_id || '',
                    club_name:            c.club_name,
                    shopify_location_id:  c.shopify_location_id,
                    soccer_location_name: c.soccer_location_name || '',
                    number_override:      !!f.number,
                    team_override:        !!f.team,
                };
            });
            const res = await authenticatedFetch('/api/settings/tp-overrides/apply', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rows: payload }),
            });
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.detail || `Apply failed (HTTP ${res.status})`);
            }
            const j = await res.json();
            let msg = `Applied ${j.applied} change${j.applied === 1 ? '' : 's'}.`;
            if ((j.resync_kicked || []).length) {
                msg += ` Team-filter data refreshing in the background for: ${j.resync_kicked.join(', ')}.`;
            }
            setNotice(msg);
            setOrig(flags);   // keep current sort — no jumpy reorder after apply
        } catch (e) {
            setErr(String(e.message || e));
        } finally {
            setSaving(false);
        }
    }, [dirtyKeys, rows, flags]);

    const visible = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            r.club_name.toLowerCase().includes(q)
            || (r.soccer_location_name || '').toLowerCase().includes(q)
            || (STORE_DISPLAY_NAMES[r.store_key] || r.store_key).toLowerCase().includes(q));
    }, [rows, filter]);

    const enabledCount = useMemo(
        () => rows.filter(r => { const f = getF(clubKey(r)); return f.number || f.team; }).length,
        [rows, getF]);

    const th = { textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 600,
                 textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6d7175',
                 borderBottom: '1px solid #e1e3e5', position: 'sticky', top: 0,
                 background: '#fff', zIndex: 1 };
    const thC = { ...th, textAlign: 'center', width: 140 };
    const td = { padding: '8px 12px', borderBottom: '1px solid #f1f2f3', fontSize: 13 };
    const tdC = { ...td, textAlign: 'center' };

    return (
        <BlockStack gap="400">
            <Card>
                <BlockStack gap="300">
                    <Text variant="headingMd" as="h2">Team Portal Overrides</Text>
                    <Text tone="subdued">
                        <b>Number Override:</b> when a player changes their number in the Team
                        Portal after ordering, the current TP number wins for picking, pressing,
                        packing, and print. <b>Team Override:</b> same rule for the player's
                        team — it also updates the order's team data so the Orders list and
                        report team filters reflect the current TP team. Check the boxes, then
                        press Apply.
                    </Text>
                </BlockStack>
            </Card>

            {err && <Banner tone="critical" title="Error" onDismiss={() => setErr(null)}><p>{err}</p></Banner>}
            {notice && <Banner tone="success" onDismiss={() => setNotice(null)}><p>{notice}</p></Banner>}

            <Card>
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center" gap="300">
                        <InlineStack gap="300" blockAlign="center">
                            <Box minWidth="280px">
                                <TextField
                                    label="Filter" labelHidden placeholder="Filter by club, location, or store…"
                                    value={filter} onChange={setFilter} clearButton
                                    onClearButtonClick={() => setFilter('')} autoComplete="off"
                                />
                            </Box>
                            <Text tone="subdued" as="span">
                                {rows.length} clubs · {enabledCount} with overrides
                            </Text>
                        </InlineStack>
                        <InlineStack gap="200" blockAlign="center">
                            {dirtyKeys.length > 0 && (
                                <Badge tone="attention">{`${dirtyKeys.length} unsaved`}</Badge>
                            )}
                            <Button
                                variant="primary"
                                disabled={dirtyKeys.length === 0 || saving}
                                loading={saving}
                                onClick={apply}
                            >
                                Apply
                            </Button>
                        </InlineStack>
                    </InlineStack>

                    {loading ? (
                        <Box padding="600"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box>
                    ) : (
                        <div style={{ maxHeight: '62vh', overflowY: 'auto',
                                      border: '1px solid #e1e3e5', borderRadius: 8 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={th}>Club</th>
                                        <th style={th}>Fulfillment Location</th>
                                        <th style={th}>Store</th>
                                        <th style={thC}>Number Override</th>
                                        <th style={thC}>Team Override</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.length === 0 && (
                                        <tr><td style={td} colSpan={5}>
                                            <Text tone="subdued">No clubs match.</Text>
                                        </td></tr>
                                    )}
                                    {visible.map(r => {
                                        const k = clubKey(r);
                                        const f = getF(k);
                                        const dirty = dirtyKeys.includes(k);
                                        return (
                                            <tr key={k} style={dirty ? { background: '#fff8e5' } : undefined}>
                                                <td style={td}><Text as="span" fontWeight={f.number || f.team ? 'semibold' : 'regular'}>{r.club_name}</Text></td>
                                                <td style={td}>{r.soccer_location_name || '—'}</td>
                                                <td style={td}>{STORE_DISPLAY_NAMES[r.store_key] || r.store_key}</td>
                                                <td style={tdC}>
                                                    <Checkbox
                                                        label="Number override" labelHidden
                                                        checked={!!f.number}
                                                        onChange={() => toggle(k, 'number')}
                                                    />
                                                </td>
                                                <td style={tdC}>
                                                    <Checkbox
                                                        label="Team override" labelHidden
                                                        checked={!!f.team}
                                                        onChange={() => toggle(k, 'team')}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </BlockStack>
            </Card>
        </BlockStack>
    );
}
